// src/pages/AppraiserSignup.js
// HomeIQa — Appraiser directory listing signup
// $5/month Basic · $19/month Featured
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import Logo from '../components/Logo';

const SPECIALTIES_OPTIONS = [
  'Pre-listing', 'PMI removal', 'Estate & probate', 'Divorce settlement',
  'Tax appeal', 'Cash sale', 'Private lending', 'Relocation'
];

export default function AppraiserSignup() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState('basic');
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', credentials: '', company: '', phone: '', email: '',
    city: '', state: 'GA', county: '', ascLicenseNumber: '',
    specialties: [], fee: '', turnaround: '', bio: ''
  });

  function update(field, val) {
    setForm(prev => ({ ...prev, [field]: val }));
  }

  function toggleSpecialty(s) {
    setForm(prev => ({
      ...prev,
      specialties: prev.specialties.includes(s)
        ? prev.specialties.filter(x => x !== s)
        : [...prev.specialties, s]
    }));
  }

  async function submit() {
    setLoading(true);
    try {
      // Save to Firestore pending verification
      await addDoc(collection(db, 'appraiser_applications'), {
        ...form,
        plan,
        status: 'pending_verification',
        verified: false,
        active: false,
        featured: plan === 'featured',
        rating: 0,
        reviewCount: 0,
        createdAt: serverTimestamp()
      });
      setSubmitted(true);
    } catch (e) {
      alert('Submission failed. Please try again or email hello@homeIQa.com');
    } finally {
      setLoading(false);
    }
  }

  if (submitted) return (
    <div style={styles.page}>
      <div style={styles.header}><Logo /></div>
      <div style={styles.successCard}>
        <div style={styles.successIcon}>✓</div>
        <h2 style={styles.successTitle}>Application received!</h2>
        <p style={styles.successText}>
          We'll verify your ASC license ({form.ascLicenseNumber}) within 1–2 business days.
          Once verified, you'll receive a payment link to activate your listing.
        </p>
        <p style={styles.successText}>
          Questions? Email <a href="mailto:hello@homeIQa.com" style={{ color: '#6B1E2E' }}>hello@homeIQa.com</a>
        </p>
        <button style={styles.doneBtn} onClick={() => navigate('/appraisers')}>
          View directory →
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}><Logo /></div>

      <div style={styles.heroBar}>
        <h1 style={styles.heroTitle}>List your practice on HomeIQa</h1>
        <p style={styles.heroSub}>Get matched with homeowners who need exactly your services. ASC verification included.</p>
      </div>

      <div style={styles.layout}>
        {/* PLAN SELECTION */}
        <div style={styles.sidebar}>
          <div style={styles.sideTitle}>Choose your plan</div>
          {[
            { id: 'basic', price: 5, name: 'Basic listing', features: ['Listed in directory', 'ASC verified badge', 'Lead notifications', 'Contact info displayed'] },
            { id: 'featured', price: 19, name: 'Featured listing', features: ['Everything in Basic', 'Appears first in results', 'Featured badge', 'Highlighted in reports', 'Priority lead routing'] }
          ].map(p => (
            <div key={p.id}
              style={{ ...styles.planCard, ...(plan === p.id ? styles.planActive : {}) }}
              onClick={() => setPlan(p.id)}>
              <div style={styles.planHeader}>
                <span style={styles.planName}>{p.name}</span>
                <span style={styles.planPrice}>${p.price}/mo</span>
              </div>
              {p.features.map(f => (
                <div key={f} style={styles.planFeature}>
                  <span style={styles.planCheck}>✓</span> {f}
                </div>
              ))}
            </div>
          ))}
          <div style={styles.sideNote}>
            Payment is collected after ASC verification. Cancel anytime — no contracts.
          </div>
        </div>

        {/* FORM */}
        <div style={styles.formCard}>
          <div style={styles.formTitle}>Your information</div>

          <div style={styles.fieldGrid}>
            <div style={styles.field}>
              <label style={styles.label}>Full name *</label>
              <input style={styles.input} value={form.name} onChange={e => update('name', e.target.value)} placeholder="David Whitmore" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Credentials (e.g. CRA, SRA)</label>
              <input style={styles.input} value={form.credentials} onChange={e => update('credentials', e.target.value)} placeholder="CRA" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Company / practice name *</label>
              <input style={styles.input} value={form.company} onChange={e => update('company', e.target.value)} placeholder="Whitmore Appraisal Group" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>ASC License number * <span style={styles.required}>(will be verified)</span></label>
              <input style={styles.input} value={form.ascLicenseNumber} onChange={e => update('ascLicenseNumber', e.target.value)} placeholder="GA-10482" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email address *</label>
              <input style={styles.input} type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@yourfirm.com" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Phone number *</label>
              <input style={styles.input} type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(404) 555-0182" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>City *</label>
              <input style={styles.input} value={form.city} onChange={e => update('city', e.target.value)} placeholder="Atlanta" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>State *</label>
              <select style={styles.select} value={form.state} onChange={e => update('state', e.target.value)}>
                {['GA','FL','TX','NC','SC','TN','AL','VA','OH','PA','NY','CA','IL','AZ','CO','WA','OR'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <label style={styles.label}>Coverage counties</label>
              <input style={styles.input} value={form.county} onChange={e => update('county', e.target.value)} placeholder="Fulton, DeKalb, Gwinnett, Cobb" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Standard fee</label>
              <input style={styles.input} value={form.fee} onChange={e => update('fee', e.target.value)} placeholder="425" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Typical turnaround</label>
              <input style={styles.input} value={form.turnaround} onChange={e => update('turnaround', e.target.value)} placeholder="3–5 business days" />
            </div>
          </div>

          <div style={styles.specialtiesSection}>
            <label style={styles.label}>Specialties (select all that apply)</label>
            <div style={styles.specialtiesGrid}>
              {SPECIALTIES_OPTIONS.map(s => (
                <button key={s}
                  style={{ ...styles.specBtn, ...(form.specialties.includes(s) ? styles.specBtnActive : {}) }}
                  onClick={() => toggleSpecialty(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Brief bio (optional)</label>
            <textarea
              style={{ ...styles.input, minHeight: 80, resize: 'vertical' }}
              value={form.bio}
              onChange={e => update('bio', e.target.value)}
              placeholder="Years of experience, areas of expertise, certifications..."
            />
          </div>

          <div style={styles.submitRow}>
            <div style={styles.submitNote}>
              By submitting, you agree to our appraiser listing terms. Your ASC license will be verified before activation. Payment is collected after verification.
            </div>
            <button
              style={styles.submitBtn}
              onClick={submit}
              disabled={loading || !form.name || !form.email || !form.ascLicenseNumber}>
              {loading ? 'Submitting...' : `Submit application — $${plan === 'featured' ? 19 : 5}/mo after verification`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#FAF7F2', fontFamily: "'DM Sans', sans-serif" },
  header: { background: '#4A1220', borderBottom: '4px solid #C9A84C', padding: '14px 40px' },
  heroBar: { background: 'white', padding: '32px 40px', borderBottom: '1px solid rgba(107,30,46,0.1)', textAlign: 'center' },
  heroTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 500, color: '#4A1220', marginBottom: 6 },
  heroSub: { fontSize: 14, color: '#5A5A5A', lineHeight: 1.6 },
  layout: { maxWidth: 960, margin: '32px auto', display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, padding: '0 24px' },
  sidebar: { alignSelf: 'start' },
  sideTitle: { fontSize: 12, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9A9A9A', marginBottom: 12 },
  planCard: { background: 'white', border: '1.5px solid rgba(107,30,46,0.12)', borderRadius: 10, padding: '18px', marginBottom: 12, cursor: 'pointer' },
  planActive: { border: '2px solid #C9A84C', background: '#FFFBF0' },
  planHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 },
  planName: { fontSize: 14, fontWeight: 500, color: '#1C1C1C' },
  planPrice: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 600, color: '#6B1E2E' },
  planFeature: { fontSize: 12, color: '#5A5A5A', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 },
  planCheck: { color: '#1D9E75', fontWeight: 700 },
  sideNote: { fontSize: 11, color: '#9A9A9A', lineHeight: 1.6, padding: '12px', background: 'white', borderRadius: 8, border: '1px solid rgba(107,30,46,0.08)' },
  formCard: { background: 'white', borderRadius: 12, padding: '28px 32px', border: '1px solid rgba(107,30,46,0.1)' },
  formTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: '#4A1220', marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid rgba(107,30,46,0.08)' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px', marginBottom: 20 },
  field: {},
  label: { display: 'block', fontSize: 12, color: '#5A5A5A', marginBottom: 5, fontWeight: 500 },
  required: { color: '#9A9A9A', fontWeight: 400 },
  input: { width: '100%', padding: '10px 13px', border: '1.5px solid rgba(107,30,46,0.15)', borderRadius: 7, fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: '#1C1C1C', outline: 'none' },
  select: { width: '100%', padding: '10px 13px', border: '1.5px solid rgba(107,30,46,0.15)', borderRadius: 7, fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: '#1C1C1C', background: 'white' },
  specialtiesSection: { marginBottom: 18 },
  specialtiesGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  specBtn: { padding: '6px 13px', border: '1px solid rgba(107,30,46,0.15)', borderRadius: 20, fontSize: 12, color: '#5A5A5A', background: 'transparent', cursor: 'pointer' },
  specBtnActive: { background: '#6B1E2E', color: 'white', border: '1px solid #6B1E2E' },
  submitRow: { borderTop: '1px solid rgba(107,30,46,0.08)', paddingTop: 18, marginTop: 8 },
  submitNote: { fontSize: 11, color: '#9A9A9A', lineHeight: 1.55, marginBottom: 14 },
  submitBtn: { width: '100%', padding: '14px', background: '#6B1E2E', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  successCard: { maxWidth: 520, margin: '64px auto', background: 'white', borderRadius: 14, padding: '48px 44px', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  successIcon: { width: 56, height: 56, borderRadius: '50%', background: '#1D9E75', color: 'white', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontWeight: 700 },
  successTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, color: '#4A1220', marginBottom: 12 },
  successText: { fontSize: 14, color: '#5A5A5A', lineHeight: 1.7, marginBottom: 12 },
  doneBtn: { marginTop: 16, padding: '12px 28px', background: '#6B1E2E', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }
};
